import { GA_TRACKING_ID } from '@/config';
import * as userSelectors from '@/user/user-selectors';
import useRegion from '@/utils/hooks/useRegion';
import { useRootTab } from '@/utils/hooks/useRootTab';
import { useEffect } from 'react';
import ReactGA from 'react-ga';
import { useSelector } from 'react-redux';

interface GATracker {
  trackingId: string;
  gaOptions: {
    name: string;
    userId: string;
  };
}

/**
 * Hook that handles Google Analytics tracking for the dashboard
 */
export function useAnalytics(): void {
  const { region } = useRegion();
  const { activeTab } = useRootTab();

  const username = useSelector(userSelectors.username);

  // Initialize the tracker when username changes
  useEffect(() => {
    if (typeof username === 'string') {
      initializeUserTracker(username);
    }
  }, [username]);

  // Track page views and user activity
  useEffect(() => {
    if (
      typeof username === 'string' &&
      typeof region === 'string' &&
      typeof activeTab === 'string'
    ) {
      trackUserNavigation(username, region, activeTab);
    }
  }, [username, region, activeTab]);
}

/**
 * Initializes new tracker in GA with user's email and sets the user as their name
 */
function initializeUserTracker(username: string): void {
  ReactGA.addTrackers([
    {
      trackingId: GA_TRACKING_ID,
      gaOptions: {
        name: 'userTracker',
        userId: username,
      },
    },
  ] as GATracker[]);

  // This sets dimension1 in GA to populate with the user's email
  ReactGA.set({ dimension1: username });
}

/**
 * Tracks user navigation in the application
 * Sends an event to GA logging which area is being accessed
 */
function trackUserNavigation(
  username: string,
  currentCity: string,
  activeNavItem: string
): void {
  const email = username;
  /* GA will not let us set an event using a user's emails, so instead this tracks the users name,
  and the city they are working with. */
  const name = email.substr(0, email.indexOf('@'));

  ReactGA.event({
    category: 'user actions',
    label: `User: ${name}, City: ${currentCity}`,
    action: `accessed the ${activeNavItem}`,
  });

  ReactGA.pageview(window.location.pathname);
}
